import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'app-session-timeout-dialog',
    template: `
        <div class="timeout-dialog">
            <!-- Animated icon -->
            <div class="icon-wrapper">
                <div class="icon-pulse"></div>
                <div class="icon-circle">
                    <mat-icon>schedule</mat-icon>
                </div>
            </div>

            <!-- Title -->
            <h2 class="dialog-title">Session Expiring Soon</h2>

            <!-- Countdown ring -->
            <div class="countdown-container">
                <svg class="countdown-ring" viewBox="0 0 100 100">
                    <circle class="ring-bg" cx="50" cy="50" r="44" />
                    <circle class="ring-progress" cx="50" cy="50" r="44"
                        [style.stroke-dashoffset]="dashOffset"
                        [class.warning]="remainingSeconds <= 30"
                        [class.critical]="remainingSeconds <= 10" />
                </svg>
                <div class="countdown-text">
                    <span class="countdown-value">{{ countdownDisplay }}</span>
                    <span class="countdown-label">{{ remainingSeconds > 60 ? 'minutes' : 'seconds' }}</span>
                </div>
            </div>

            <!-- Message -->
            <p class="dialog-message">
                You've been inactive for a while. Your session will expire automatically for security.
            </p>

            <!-- Actions -->
            <div class="dialog-actions">
                <button mat-stroked-button class="logout-btn" (click)="onLogout()">
                    <mat-icon>exit_to_app</mat-icon>
                    Logout
                </button>
                <button mat-raised-button class="continue-btn" (click)="onContinue()">
                    <mat-icon>verified_user</mat-icon>
                    Continue Session
                </button>
            </div>
        </div>
    `,
    styles: [`
        :host {
            display: block;
        }

        .timeout-dialog {
            text-align: center;
            padding: 8px 16px 16px;
        }

        /* Animated icon */
        .icon-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 12px;
        }

        .icon-circle {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: linear-gradient(135deg, #ff6b35, #f7931e);
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            z-index: 1;
        }

        .icon-circle mat-icon {
            color: #fff;
            font-size: 28px;
            width: 28px;
            height: 28px;
        }

        .icon-pulse {
            position: absolute;
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: rgba(255, 107, 53, 0.25);
            animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(1.5); opacity: 0; }
        }

        /* Title */
        .dialog-title {
            font-size: 20px;
            font-weight: 600;
            color: #2d3748;
            margin: 0 0 20px;
            letter-spacing: -0.3px;
        }

        /* Countdown ring */
        .countdown-container {
            position: relative;
            width: 120px;
            height: 120px;
            margin: 0 auto 20px;
        }

        .countdown-ring {
            width: 100%;
            height: 100%;
            transform: rotate(-90deg);
        }

        .ring-bg {
            fill: none;
            stroke: #edf2f7;
            stroke-width: 6;
        }

        .ring-progress {
            fill: none;
            stroke: #f7931e;
            stroke-width: 6;
            stroke-linecap: round;
            stroke-dasharray: 276.46;
            transition: stroke-dashoffset 1s linear, stroke 0.5s ease;
        }

        .ring-progress.warning {
            stroke: #e53e3e;
        }

        .ring-progress.critical {
            stroke: #c53030;
            animation: urgentPulse 0.8s ease-in-out infinite;
        }

        @keyframes urgentPulse {
            0%, 100% { stroke-opacity: 1; }
            50% { stroke-opacity: 0.5; }
        }

        .countdown-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .countdown-value {
            font-size: 32px;
            font-weight: 700;
            color: #2d3748;
            line-height: 1;
        }

        .countdown-label {
            font-size: 11px;
            color: #a0aec0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 2px;
        }

        /* Message */
        .dialog-message {
            font-size: 13px;
            color: #718096;
            line-height: 1.6;
            margin: 0 0 24px;
            padding: 0 8px;
        }

        /* Actions */
        .dialog-actions {
            display: flex;
            gap: 12px;
            justify-content: center;
        }

        .logout-btn {
            color: #718096 !important;
            border-color: #e2e8f0 !important;
            padding: 0 20px;
            height: 42px;
            font-weight: 500;
        }

        .logout-btn mat-icon {
            font-size: 18px;
            width: 18px;
            height: 18px;
            margin-right: 4px;
        }

        .continue-btn {
            background: linear-gradient(135deg, #48bb78, #38a169) !important;
            color: #fff !important;
            padding: 0 24px;
            height: 42px;
            font-weight: 600;
            box-shadow: 0 2px 8px rgba(72, 187, 120, 0.35) !important;
        }

        .continue-btn mat-icon {
            font-size: 18px;
            width: 18px;
            height: 18px;
            margin-right: 4px;
        }

        .continue-btn:hover {
            box-shadow: 0 4px 14px rgba(72, 187, 120, 0.5) !important;
        }
    `]
})
export class SessionTimeoutDialogComponent implements OnInit, OnDestroy {
    countdownDisplay = '';
    remainingSeconds = 0;
    dashOffset = 0;
    private totalSeconds = 0;
    private intervalId: any = null;
    private readonly CIRCUMFERENCE = 276.46; // 2 * PI * 44

    constructor(
        public dialogRef: MatDialogRef<SessionTimeoutDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { minutes: number }
    ) {
        this.totalSeconds = (data.minutes || 5) * 60;
        this.remainingSeconds = this.totalSeconds;
    }

    ngOnInit(): void {
        this.updateDisplay();
        this.intervalId = setInterval(() => {
            this.remainingSeconds--;
            if (this.remainingSeconds <= 0) {
                clearInterval(this.intervalId);
                this.dialogRef.close('logout');
                return;
            }
            this.updateDisplay();
        }, 1000);
    }

    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    private updateDisplay(): void {
        const min = Math.floor(this.remainingSeconds / 60);
        const sec = this.remainingSeconds % 60;

        // Show mm:ss when > 60s, just seconds when <= 60s
        if (this.remainingSeconds > 60) {
            this.countdownDisplay = `${min}:${sec.toString().padStart(2, '0')}`;
        } else {
            this.countdownDisplay = `${this.remainingSeconds}`;
        }

        // Update ring progress
        const progress = this.remainingSeconds / this.totalSeconds;
        this.dashOffset = this.CIRCUMFERENCE * (1 - progress);
    }

    onContinue(): void {
        this.dialogRef.close('continue');
    }

    onLogout(): void {
        this.dialogRef.close('logout');
    }
}
